import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import PersonAvatar, {
	PersonAvatarSize,
} from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import EvaluationAnswerComposer from './EvaluationAnswerComposer';
import EvaluationSubmissionItemDisplay from './display/EvaluationSubmissionItemDisplay';
import CompetencyDisplay from './display/CompetencyDisplay';
import PlaceholderDisplay from './display/PlaceholderDisplay';
import EvaluationAnswerCard from './EvaluationAnswerCard';
import useDetectPrint from '../hooks/useDetectPrint';

const Bubble = styled.div<{
	alternate?: boolean;
}>`
	flex-shrink: 1;
	background: ${(props) => (props.alternate ? '#E5F2FB' : 'white')};
	border-radius: 4px;
	padding: 10px;
	min-width: 50%;
`;

const Name = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
`;

export default function EvaluationSubmissionAnswer(props: {
	section: number;
	item: number;
	metadata?: { goal: graphql.EvaluationSubmissionGoal };
	submission: graphql.EvaluationSubmission;
	party: graphql.EvaluationSubmissionParty;
	relevant: boolean;
	onSave: () => void;
	isSaving: boolean;
}) {
	const { t } = useTranslation();
	const isPrinting = useDetectPrint();

	const result = props.submission[props.party.toLowerCase() as 'employee' | 'manager'];

	const alternate = props.party === graphql.EvaluationSubmissionParty.Manager;

	if (result.viewerCanUpdate && props.relevant && !isPrinting) {
		return (
			<Box paddingY={2}>
				<EvaluationAnswerComposer
					submission={props.submission}
					section={props.section}
					item={props.item}
					metadata={props.metadata}
					result={result}
					onSave={props.onSave}
					isSaving={props.isSaving}
					side={props.party === graphql.EvaluationSubmissionParty.Employee ? 'left' : 'right'}
				/>
			</Box>
		);
	}

	const item = props.submission.evaluationForm.sections[props.section].items[props.item];

	const answer = props.submission.viewer.sections[props.section].items[props.item];

	if (item.type === graphql.EvaluationSectionItemType.Competency) {
		const isEmployee = props.party === graphql.EvaluationSubmissionParty.Employee;
		return (
			<EvaluationAnswerCard user={result.user} side={isEmployee ? 'left' : 'right'} withAvatar>
				<CompetencyDisplay
					party={props.party}
					submission={props.submission}
					section={props.section}
					item={props.item}
				/>
			</EvaluationAnswerCard>
		);
	} else if (item.type === graphql.EvaluationSectionItemType.ReviewGoals) {
		const { goal } = props.metadata!;

		const index = answer?.reviewGoals?.findIndex(({ goalID }) => goalID === goal.goal.id);
		const enabled = index !== undefined && index >= 0;

		if (!enabled && props.submission.viewer.party === props.party) {
			return null;
		}
	}

	return (
		<Flex alignItems="center">
			<Flex
				paddingY={2}
				flexDirection={alternate ? 'row-reverse' : 'row'}
				flexGrow={1}
				flexShrink={1}
			>
				<Box flexShrink={0} flexGrow={0}>
					<PersonAvatar person={result.user} size={PersonAvatarSize.Medium} />
				</Box>
				<Box flexShrink={0} flexGrow={0} width={10} />
				<Bubble alternate={alternate}>
					<Name>
						{result.user.firstName} {result.user.lastName}
					</Name>
					{props.relevant ? (
						<EvaluationSubmissionItemDisplay
							form={item}
							item={result.sections[props.section].items[props.item]!}
							metadata={props.metadata}
						/>
					) : (
						<PlaceholderDisplay
							text={t(`evaluation.submission.irrelevant.short.${item.fillInBy.toLowerCase()}`)}
						/>
					)}
				</Bubble>
				<Box flexGrow={1} />
			</Flex>
		</Flex>
	);
}
